var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import BemClassBuilder from '../../../core/utils/src/class-builders/bem';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { Button } from '../../button/src';
import LayoutUpdater from './layout/updater';
import { isMobileView } from './layout/utils';
import NavigationActionHandler from './layout/navigationActionHandler';
import { ToggleIcon } from './toggle-icon';
function handlePropertyChange() {
    if (this.__componentState === 'attached') {
        this.layoutUpdater.updateLayout();
    }
}
var NAVIGATION_CONTAINER_CLASSNAME = 'awsui-app-layout__navigation';
export var AppLayout = AwsUi.__addComponent('awsui-app-layout', {
    releaseStatus: 'stable',
    properties: {
        // Determines special treatment for the content, suitable for a particular type. Based on the content type, we
        // adjust width of the content region and the initial state of navigation panel (open or closed).
        // The default values for all content types can be found in [this file](https://code.amazon.com/packages/AWS-UI-Components/blobs/mainline-2.1/--/components/app-layout/src/layout/defaults.ts#L1).
        //
        // If you have a property navigationOpen, minContentWidth or maxContentWidth explicitly set, it will take precedence
        // over the default coming from content type.
        contentType: {
            type: 'string',
            defaultValue: 'default',
            valid: ['default', 'form', 'table', 'cards', 'wizard', 'no-paddings'],
            onChange: handlePropertyChange
        },
        // State of the navigation drawer.
        navigationOpen: {
            // We cannot have a default here, because it is closed on mobile by default
            // and on desktop it is open only when there is enough space
            nullable: true,
            type: 'boolean',
            onChange: function () {
                this.__navigationWasDefaulted = false;
                if (this.__componentState === 'attached') {
                    this.layoutUpdater.updateLayout();
                }
            }
        },
        // Navigation drawer width in pixels.
        navigationWidth: {
            type: 'integer',
            defaultValue: 280,
            onChange: handlePropertyChange
        },
        // If `true`, the navigation drawer is not displayed at all.
        navigationHide: {
            type: 'boolean',
            onChange: handlePropertyChange
        },
        // If `true`, the notification region is rendered above the scrollable
        // content area so it is always visible.
        stickyNotifications: {
            type: 'boolean',
            defaultValue: false
        },
        // State of the tools drawer.
        toolsOpen: {
            type: 'boolean',
            defaultValue: false,
            onChange: handlePropertyChange
        },
        // If `true`, the tools drawer is not displayed at all.
        toolsHide: {
            type: 'boolean',
            onChange: handlePropertyChange
        },
        // Tools drawer width in pixels.
        toolsWidth: {
            type: 'integer',
            defaultValue: 290,
            onChange: handlePropertyChange
        },
        // Maximum main content panel width in pixels.
        maxContentWidth: {
            type: 'integer',
            onChange: handlePropertyChange
        },
        // Minimum main content panel width in pixels.
        minContentWidth: {
            type: 'integer',
            onChange: handlePropertyChange
        },
        // CSS selector for the application header.
        // The default value reflects Mezzanine header.
        headerSelector: {
            type: 'string',
            defaultValue: '#b #h',
            onChange: handlePropertyChange
        },
        // CSS selector for the application footer.
        // The default value reflects Mezzanine footer.
        footerSelector: {
            type: 'string',
            defaultValue: '#b #f',
            onChange: handlePropertyChange
        },
        // Aria labels for the drawer operating buttons.
        // Please use this property in order to enhance the
        // accessibility of your console.
        //
        // * `navigation` [string]: Label for the landmark that wraps the navigation drawer. *For example: Navigation drawer*
        // * `navigationToggle` [string]: Label for the button that opens the navigation drawer.
        //                                *For example: Open navigation drawer*
        // * `navigationClose` [string]: Label for the button that closes the navigation drawer.
        //                               *For example: Close navigation drawer*
        // * `notifications` [string]: Label for the notifications region. *For example: Notifications*
        // * `tools` [string]: Label for the landmark that wraps the tools drawer. *For example: Help panel*
        // * `toolsToggle` [string]: Label for the button that opens the tools drawer. *For example: Open help panel*
        // * `toolsClose` [string]: Label for the button that closes the tools drawer. *For example: Close help panel*
        labels: {
            type: 'object',
            objectType: 'AppLayout.Labels'
        },
        // This flag captures the current view of app layout,
        // rerender can be triggered by setting this flag.
        __isMobile: {
            type: 'boolean'
        },
        // This internal flag states that the navigationOpen
        // property was defaulted by updateLayout.
        __navigationWasDefaulted: {
            type: 'boolean',
            defaultValue: false
        }
    },
    events: {
        // Fired when the navigation drawer is toggled.
        navigationChange: {
            cancelable: false,
            detailType: 'AppLayout.ChangeDetail'
        },
        // Fired when the tools drawer is toggled.
        toolsChange: {
            cancelable: false,
            detailType: 'AppLayout.ChangeDetail'
        }
    },
    regions: {
        // Navigation drawer.
        navigation: {},
        // Main content.
        content: {},
        // Tools drawer.
        tools: {},
        // Displayed on top of the main content in the scrollable area,
        // it occupies the full width and is not affected by the padding that is added to the content region.
        // Conceived to contain notifications (flash messages).
        notifications: {},
        // Breadcrumbs should be defined whithin this region in order to benefit from the
        // responsive breadcrumb pattern.
        breadcrumbs: {}
    },
    callbacks: {
        initialized: function () {
            this.__isMobile = isMobileView();
            this.__openNavigation = this.__openNavigation.bind(this);
            this.__openTools = this.__openTools.bind(this);
            this.__closeNavigation = this.__closeNavigation.bind(this);
            this.__closeTools = this.__closeTools.bind(this);
        },
        attached: function () {
            var _this = this;
            // on attached, because it is finding DOM nodes on creation
            this.layoutUpdater = new LayoutUpdater(this);
            this.layoutUpdater.attach();
            this.navigationActionHandler = new NavigationActionHandler(this.node.querySelector("." + NAVIGATION_CONTAINER_CLASSNAME), function () { return _this.layoutUpdater.closeNavigationIfNecessary(); });
            this.navigationActionHandler.attach();
        },
        detached: function () {
            this.navigationActionHandler.detach();
            this.layoutUpdater.detach();
        }
    },
    functions: {
        __focusCloseButton: function (drawerName) {
            // We focus the close button instead of the whole landmark region for two reasons:
            // 1) Focusing the close button makes it more clear to the user that the drawers are controlled by toggle buttons with
            //    expanded and collapsed states. It also makes it easier to close when it was open by accident.
            // 2) Some screen readers like NVDA like to read out the entire content of a region when the focus has been programatically
            //    put there. This can be annoying as a user to be confronted with a wall of text when opening a navigation menu.
            //    The screen readers will still announce that the close button is inside a navigation/tools region for orientation.
            var button = this.node.querySelector(".awsui-app-layout__" + drawerName + " .awsui-app-layout__close-button");
            if (button) {
                button.component.focus();
            }
        },
        __focusToggleButton: function (drawerName) {
            var button;
            if (this.__isMobile) {
                button = this.node.querySelector(".awsui-app-layout__toggle--" + drawerName + "-mobile awsui-button");
            }
            else {
                button = this.node.querySelector(".awsui-app-layout__toggle--" + drawerName + " awsui-button");
            }
            if (button) {
                button.component.focus();
            }
        },
        __openNavigation: function (event) {
            this.navigationOpen = true;
            if (event) {
                this.__focusCloseButton('navigation');
            }
            this.__fireEvent('navigationChange', { open: true });
            if (event) {
                event.stopPropagation();
            }
        },
        __closeNavigation: function (event) {
            this.navigationOpen = false;
            if (event) {
                this.__focusToggleButton('navigation');
            }
            this.__fireEvent('navigationChange', { open: false });
        },
        // Opens the tools panel if it is not already open. Note that it is preferable to
        // control the state by listening to `toolsChange` and providing `toolsOpen`.
        openTools: function () {
            this.toolsOpen = true;
            this.__focusCloseButton('tools');
            this.__fireEvent('toolsChange', { open: true });
        },
        __openTools: function (event) {
            this.openTools();
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }
        },
        __closeTools: function () {
            this.toolsOpen = false;
            this.__focusToggleButton('tools');
            this.__fireEvent('toolsChange', { open: false });
        },
        // Manually opens the navigation drawer if it is necessary for the current viewport size.
        openNavigationIfNecessary: function () {
            this.layoutUpdater.openNavigationIfNecessary();
        },
        // Manually closes the navigation drawer if it is necessary for the current viewport size.
        closeNavigationIfNecessary: function () {
            this.layoutUpdater.closeNavigationIfNecessary();
        },
        // Updates component sizes. Call this function whenever you do changes in the surrounding HTML, for example, change
        // the page header or footer. This function is automatically called after page load and on window resize event
        updateLayout: function () {
            this.layoutUpdater.updateLayout();
        }
    },
    render: function (m, h) {
        var _this = this;
        var getLabel = function (label) {
            if (!_this.labels) {
                return;
            }
            return _this.labels[label];
        };
        var closeButton = function (action, label) { return (AwsUi.__createElement(Button, { label: label, className: "awsui-app-layout__close-button", variant: "icon", click: function (e) { return action(e); }, icon: "close", formAction: "none" })); };
        var breadcrumbs = h.region('breadcrumbs', 'div', { className: 'awsui-app-layout__breadcrumbs' });
        var mobileToggle = function () {
            if (!_this.__isMobile) {
                return null;
            }
            var mobileDrawerOpen = _this.navigationOpen || _this.toolsOpen;
            var navigationToggle = function () { return (AwsUi.__createElement("nav", { className: new BemClassBuilder(_this, 'toggle-landmark').modifier('navigation').toString(), "aria-hidden": "" + !!mobileDrawerOpen, "aria-label": getLabel('navigation') },
                AwsUi.__createElement(ToggleIcon, { icon: "menu", onClick: _this.__openNavigation, label: getLabel('navigationToggle'), extraClass: "awsui-app-layout__toggle--navigation-mobile", disabled: mobileDrawerOpen }))); };
            var toolsToggle = function () { return (AwsUi.__createElement("aside", { className: new BemClassBuilder(_this, 'toggle-landmark').modifier('tools').toString(), "aria-hidden": "" + !!mobileDrawerOpen, "aria-label": getLabel('tools') },
                AwsUi.__createElement(ToggleIcon, { icon: "status-info", onClick: _this.__openTools, label: getLabel('toolsToggle'), extraClass: "awsui-app-layout__toggle--tools-mobile", disabled: mobileDrawerOpen }))); };
            return (AwsUi.__createElement("div", { className: "awsui-app-layout__toggle--mobile" },
                !_this.navigationHide ? navigationToggle() : null,
                breadcrumbs,
                !_this.toolsHide ? toolsToggle() : null));
        };
        // We are using two landmarks per drawer, i.e. two NAVs and two ASIDEs, because of several
        // known bugs in NVDA that cause focus changes within a container to sometimes not be
        // announced. As a result, we use one region for the open button and one region for the
        // actual drawer content, always hiding the other one when it's not visible.
        // An alternative solution to follow a more classic implementation here to only have one
        // button that triggers the opening/closing of the drawer also did not work due to another
        // series of bugs in NVDA (together with Firefox) which prevent the changed expanded state
        // from being announced.
        // Even with this workaround in place, the announcement of the close button when opening a
        // panel in NVDA is not working correctly. The suspected root cause is one of the bugs below
        // as well.
        // Relevant tickets:
        // * https://github.com/nvaccess/nvda/issues/6606
        // * https://github.com/nvaccess/nvda/issues/5825
        // * https://github.com/nvaccess/nvda/issues/5247
        // * https://github.com/nvaccess/nvda/pull/8869 (reverted PR that was going to fix it)
        var desktopNavigationToggle = function () {
            return !_this.__isMobile && !_this.navigationHide ? (AwsUi.__createElement("nav", { className: 'awsui-app-layout__toggle--navigation', "aria-hidden": "" + !!_this.navigationOpen, "aria-label": getLabel('navigation'), onclick: _this.__openNavigation },
                AwsUi.__createElement(ToggleIcon, { icon: "menu", onClick: _this.__openNavigation, label: getLabel('navigationToggle'), disabled: _this.navigationOpen }))) : null;
        };
        var desktopToolsToggle = function () {
            return !_this.__isMobile && !_this.toolsHide ? (AwsUi.__createElement("aside", { className: "awsui-app-layout__toggle--tools", "aria-hidden": "" + !!_this.toolsOpen, "aria-label": getLabel('tools'), onclick: _this.__openTools },
                AwsUi.__createElement(ToggleIcon, { icon: "status-info", onClick: _this.__openTools, label: getLabel('toolsToggle'), disabled: _this.toolsOpen }))) : null;
        };
        var stickyNotifications = this.__isMobile ? false : !!this.stickyNotifications;
        var notificationRegion = function () {
            var classes = new LegacyClassBuilder(_this)
                .addClass('awsui-app-layout__notifications')
                .addClass('awsui-app-layout__notifications--sticky', stickyNotifications);
            var label = getLabel('notifications');
            var props = {
                role: 'region'
            };
            if (label) {
                props['aria-label'] = label;
            }
            return (AwsUi.__createElement("div", __assign({}, props, { className: classes.toClassName() }), h.region('notifications', 'div')));
        };
        var classes = new LegacyClassBuilder(this)
            .addClass('awsui-app-layout')
            .addPropertyValueClass('contentType')
            .addClass('awsui-app-layout--mobile', this.__isMobile)
            .toClassName();
        return (AwsUi.__createElement("div", { className: classes },
            mobileToggle(),
            AwsUi.__createElement("div", { className: "" + NAVIGATION_CONTAINER_CLASSNAME },
                AwsUi.__createElement("nav", { className: "awsui-app-layout__navigation-landmark", "aria-label": getLabel('navigation'), "aria-hidden": "" + !this.navigationOpen },
                    closeButton(this.__closeNavigation, getLabel('navigationClose')),
                    h.region('navigation'))),
            desktopNavigationToggle(),
            AwsUi.__createElement("main", { className: "awsui-app-layout__container" },
                stickyNotifications && notificationRegion(),
                AwsUi.__createElement("div", { className: "awsui-app-layout__content" },
                    !stickyNotifications && notificationRegion(),
                    AwsUi.__createElement("div", { className: "awsui-app-layout__content--scrollable" },
                        !this.__isMobile ? breadcrumbs : null,
                        h.region('content')))),
            AwsUi.__createElement("div", { className: "awsui-app-layout__tools" },
                AwsUi.__createElement("aside", { className: "awsui-app-layout__tools-landmark", "aria-label": getLabel('tools'), "aria-hidden": "" + !this.toolsOpen },
                    closeButton(this.__closeTools, getLabel('toolsClose')),
                    h.region('tools'))),
            desktopToolsToggle()));
    }
});
//# sourceMappingURL=index.js.map